import React from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip } from '@material-ui/core';
import { Box, Stepper, Step, StepLabel } from '@mui/material';
import Alert from '@material-ui/lab/Alert';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import { useDispatch, useSelector } from 'react-redux';
import {
    buildCreateActivityPricingValidationData,
    CREATE_ACTIVITY_PRICING_STEPS,
    CREATE_ACTIVITY_PRICING_STEP_INDEX,
    CREATE_ACTIVITY_PRICING_STEP_WARNINGS,
    CREATE_ACTIVITY_PRICING_STEP_KEYS
} from './createActivityAndPricingConsts';
import {
    setCurrentStep,
    cleanupStepDraft,
    resetCreateActivityAndPricingState
} from './createActivityAndPricingStateSlice';
import Authorize from 'components/Authorize';
import { permissionProfiles } from 'components/Authorize/permissionProfiles';
import LoadingSpinner from 'components/LoadingSpinner';
import { useCreateActivityDialogStyles } from './CreateActivityAndPricingDialogStyle';
import useGetActivityData from './hooks/useGetActivityData';
import { createActivitySave, updateActivitySave } from './utils/activitySaveOperations';
import { hasAnyStepChanges } from './utils/editMappingUtils';
import { useCreateActivityPricingManagementMutation, useUpdateActivityPricingManagementMutation } from 'features/pricing/pricingSlice';

const MODES = {
    CREATE: 'create',
    EDIT: 'edit',
};

const ActivityPricingManagementDialog = ({ open, handleClose, mode = MODES.CREATE, activityItemId = null, prefill = null }) => {
    const classes = useCreateActivityDialogStyles();
    const dispatch = useDispatch();
    const isEditMode = mode === MODES.EDIT;

    const onCloseDialog = () => {
        handleClose();
        dispatch(resetCreateActivityAndPricingState());
    };

    const { isDataLoaded, serverPricing, serverRules } = useGetActivityData({
        activityItemId: activityItemId,
        open,
        enabled: isEditMode,
        prefill,
    });

    // RTK Query mutations
    const [createActivityPricingManagement, { isLoading: isCreating }] = useCreateActivityPricingManagementMutation();
    const [updateActivityPricingManagement, { isLoading: isUpdating }] = useUpdateActivityPricingManagementMutation();

    // Get state from Redux
    const currentStep = useSelector(state => state.createActivityAndPricingState.currentStep);
    const stepsData = useSelector(state => state.createActivityAndPricingState.steps);
    const originalSteps = useSelector(state => state.createActivityAndPricingState.originalSteps);
    const isEditInitialized = useSelector(state => state.createActivityAndPricingState.isEditMode && state.createActivityAndPricingState.editActivityId === activityItemId);

    // In edit mode, wait for both query AND Redux initialization
    const isFullyLoaded = isEditMode ? (isDataLoaded && isEditInitialized) : true;

    // Check for changes in edit mode
    const hasChanges = isEditMode ? hasAnyStepChanges(originalSteps, stepsData) : true;

    // Save handlers
    const handleSave = async () => {
        if (isEditMode) {
            await updateActivitySave({
                stepsData,
                originalSteps,
                activityItemId,
                updateMutation: updateActivityPricingManagement,
                onSuccess: onCloseDialog
            });
        } else {
            await createActivitySave({
                stepsData,
                createMutation: createActivityPricingManagement,
                onSuccess: onCloseDialog
            });
        }
    };

    const isSaving = isEditMode ? isUpdating : isCreating;
    const saveDisabled = isEditMode && !hasChanges;
    const saveDisabledTooltip = saveDisabled ? "No changes to save" : "";
    
    const activityName = stepsData[CREATE_ACTIVITY_PRICING_STEP_KEYS.ACTIVITY_DETAILS]?.name || '';
    const title = isEditMode 
        ? `Edit Activity & Pricing${activityName ? ` - ${activityName}` : ''}` 
        : "Create Activity & Pricing";
    
    const saveButtonText = isEditMode ? "Save Changes" : "Create activity";
    const savingButtonText = isEditMode ? "Saving..." : "Creating...";

    const currentStepConfig = CREATE_ACTIVITY_PRICING_STEPS[currentStep];

    const moveToStep = (nextStep) => {
        dispatch(setCurrentStep(nextStep));
    };

    const validationData = buildCreateActivityPricingValidationData(stepsData);
    const isStepValid = currentStepConfig?.validate ? currentStepConfig.validate(validationData) : true;
    const isEditingPricing = validationData.pricing?.pricingItems?.some(item => item.isNew) || validationData.pricing?.somePricingHasUnsavedEdits;
    const isEditingRule = validationData.rules?.rulesList?.some(rule => rule.isNew) || validationData.rules?.someRuleHasUnsavedEdits;
    const hasNoPricingConfigured = (validationData.pricing?.pricingItems?.length || 0) === 0;
    const hasNoRulesConfigured = (validationData.rules?.rulesList?.length || 0) === 0;

    const handleContinueOrSkip = () => {
        if (!isStepValid) {
            if (currentStep === CREATE_ACTIVITY_PRICING_STEP_INDEX.PRICING) {
                dispatch(cleanupStepDraft({ stepKey: CREATE_ACTIVITY_PRICING_STEP_KEYS.PRICING }));
            } else if (currentStep === CREATE_ACTIVITY_PRICING_STEP_INDEX.RULES) {
                dispatch(cleanupStepDraft({ stepKey: CREATE_ACTIVITY_PRICING_STEP_KEYS.RULES }));
            }
        }
        moveToStep(currentStep + 1);
    };

    const getStepWarning = () => {
        if (currentStep === CREATE_ACTIVITY_PRICING_STEP_INDEX.PRICING && isEditingPricing) {
            return CREATE_ACTIVITY_PRICING_STEP_WARNINGS.PRICING_EDITING;
        }
        if (currentStep === CREATE_ACTIVITY_PRICING_STEP_INDEX.PRICING && hasNoPricingConfigured) {
            return CREATE_ACTIVITY_PRICING_STEP_WARNINGS.NO_PRICING_CONFIGURED;
        }
        if (currentStep === CREATE_ACTIVITY_PRICING_STEP_INDEX.RULES && isEditingRule) {
            return CREATE_ACTIVITY_PRICING_STEP_WARNINGS.RULE_EDITING;
        }
        if (currentStep === CREATE_ACTIVITY_PRICING_STEP_INDEX.RULES && hasNoRulesConfigured) {
            return CREATE_ACTIVITY_PRICING_STEP_WARNINGS.NO_RULES_CONFIGURED;
        }
        return CREATE_ACTIVITY_PRICING_STEP_WARNINGS.MISSING_FIELDS;
    };

    const totalSteps = CREATE_ACTIVITY_PRICING_STEPS.length;

    const tooltipText =
        currentStep === CREATE_ACTIVITY_PRICING_STEP_INDEX.ACTIVITY && !isStepValid
            ? CREATE_ACTIVITY_PRICING_STEP_WARNINGS.ACTIVITY_REQUIRED
            : !isStepValid
                ? getStepWarning()
                : '';

    return (
        <Authorize profile={permissionProfiles.PRICING.MANAGE_PPRICING}>
            <Dialog maxWidth='lg' fullWidth open={open} onClose={onCloseDialog}>
                <DialogTitle>{title}</DialogTitle>
                <DialogContent className={classes.dialogContent} style={{ minHeight: '400px', paddingTop: 16 }}>
                    {isEditMode && !isFullyLoaded ? (
                        <LoadingSpinner loading={true} />
                    ) : (
                        <>
                            <Box className={classes.stepperContainer}>
                                <Stepper activeStep={currentStep}>
                                    {CREATE_ACTIVITY_PRICING_STEPS.map((stepConfig) => (
                                        <Step key={stepConfig.key}>
                                            <StepLabel>
                                                <div>{stepConfig.title}</div>
                                                <div className={classes.stepSubtitle}>{stepConfig.subtitle}</div>
                                            </StepLabel>
                                        </Step>
                                    ))}
                                </Stepper>
                            </Box>
                            {currentStepConfig?.component({
                                onEditStep: moveToStep
                            })}
                        </>
                    )}
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                    {!isFullyLoaded ? null : (
                        <>
                            {currentStep !== 0 && (
                                <Button
                                    className={classes.backButton}
                                    startIcon={<ArrowBackIosIcon />}
                                    onClick={() => moveToStep(currentStep - 1)}
                                >
                                    Back
                                </Button>
                            )}
                            {!isStepValid && currentStep !== 0 && currentStep !== totalSteps - 1 && (
                                <Alert severity="warning" className={classes.warningAlert}>
                                    {getStepWarning()}
                                </Alert>
                            )}
                            <Box className={classes.rightButtonContainer}>
                                {currentStep !== totalSteps - 1 ? (
                                    <Tooltip
                                        title={tooltipText}
                                        placement="top"
                                    >
                                        <span>
                                            <Button
                                                className={classes.continueButton}
                                                variant='contained'
                                                endIcon={<ArrowForwardIosIcon />}
                                                onClick={handleContinueOrSkip}
                                                disabled={currentStep === CREATE_ACTIVITY_PRICING_STEP_INDEX.ACTIVITY && !isStepValid}
                                            >
                                                {isStepValid || currentStep === CREATE_ACTIVITY_PRICING_STEP_INDEX.ACTIVITY ? 'Continue' : 'Skip'}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                ) : (
                                    <Tooltip
                                        title={saveDisabledTooltip}
                                        placement="top"
                                    >
                                        <span>
                                            <Button
                                                className={classes.continueButton}
                                                variant='contained'
                                                onClick={handleSave}
                                                disabled={isSaving || saveDisabled}
                                            >
                                                {isSaving ? savingButtonText : saveButtonText}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )}
                            </Box>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </Authorize>
    );
};

export { MODES as ACTIVITY_PRICING_DIALOG_MODES };
export default ActivityPricingManagementDialog;
